import * as Headless from '@headlessui/react'
import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import React from 'react'
import { Button } from 'V2.0/common/button'
import { ErrorMessage } from 'V2.0/common/fieldset'
import { Input } from 'V2.0/common/input'

type ArrayInputProps = {
  values: string[]
  onChange: (values: string[]) => void
  errors?: (string | undefined)[]
  placeholder?: string
  addLabel?: string
  className?: string
  disabled?: boolean
  inputProps?: Omit<React.ComponentPropsWithoutRef<typeof Input>, 'value' | 'onChange' | 'disabled' | 'invalid'>
}

export function ArrayInput({
  values,
  onChange,
  errors,
  placeholder = '',
  addLabel = 'Add',
  className,
  disabled = false,
  inputProps,
}: ArrayInputProps) {
  function handleAdd() {
    onChange([...values, ''])
  }

  function handleRemove(index: number) {
    onChange(values.filter((_, i) => i !== index))
  }

  function handleChange(index: number, value: string) {
    const next = [...values]
    next[index] = value
    onChange(next)
  }

  return (
    <div data-slot="control" className={clsx('space-y-2', className)}>
      {values.map((value, index) => {
        const error = errors?.[index]
        return (
          <div key={index}>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  {...inputProps}
                  invalid={!!error}
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
                  placeholder={placeholder}
                  disabled={disabled}
                />
              </div>
              <Headless.Button
                disabled={disabled}
                onClick={() => handleRemove(index)}
                className={clsx(
                  'relative inline-flex size-8 shrink-0 items-center justify-center rounded-lg',
                  'text-zinc-400 data-hover:text-zinc-600 dark:text-zinc-500 dark:data-hover:text-zinc-300',
                  'data-hover:bg-zinc-950/5 dark:data-hover:bg-white/10',
                  'focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',
                  'data-disabled:opacity-50',
                  'cursor-pointer'
                )}
              >
                <XMarkIcon className="size-4" />
              </Headless.Button>
            </div>
          </div>
        )
      })}
      <Button plain disabled={disabled} onClick={handleAdd} className="cursor-pointer">
        <PlusIcon />
        {addLabel}
      </Button>
    </div>
  )
}
